import Icon from '@components/Icon';
import FacebookLike from '@components/Likes/FacebookLike';
import TelegramLike from '@components/Likes/TelegramLike';
import TwitterLike from '@components/Likes/TwitterLike';
import VkLike from '@components/Likes/VkLike';
import classNames from 'classnames';
import type {MouseEvent as ReactMouseEvent, ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';

interface Props {
    link?: string;
    classes?: string;
    title?: string;
    showVia?: boolean;
    isHorizontal?: boolean;
}

const DESKTOP_WIDTH = 1200;

export default function Likes(props: Props): ReactElement<Props> {
    const [isExpanded, setIsExpanded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const classes = classNames({
        likes: true,
        'likes--expanded': isExpanded,
        'likes--horizontal': props.isHorizontal,
        [props.classes!]: props.classes,
    });
    const link = props.link ?? `${location.origin}${location.pathname}`;

    useEffect(() => {
        if (window.innerWidth >= DESKTOP_WIDTH) {
            setIsExpanded(true);
        }

        if (!props.isHorizontal) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            if (!props.isHorizontal) {
                document.removeEventListener('click', handleClickOutside);
            }
        };
    }, []);

    function handleArrowClick(event: ReactMouseEvent<HTMLDivElement>) {
        event.stopPropagation();
        setIsExpanded(!isExpanded);
    }

    function handleClickOutside(event: MouseEvent) {
        if (
            event.target !== containerRef.current &&
            !containerRef.current?.contains(event.target as Element)
        ) {
            setIsExpanded(false);
        }
    }

    return (
        <div className={classes}>
            <div ref={containerRef} className="likes__content">
                <div className="likes__arrow js-track-like-arrow" onClick={handleArrowClick}>
                    <Icon name="likes-arrow" className="likes__icon-arrow" />
                </div>
                <div className="likes__list">
                    <TelegramLike link={link} />
                    <VkLike link={link} />
                    <FacebookLike link={link} />
                    <TwitterLike
                        link={link}
                        title={
                            props.title ??
                            getContent(document.querySelector('meta[property="og:title"]'))
                        }
                        showVia={props.showVia ?? false}
                    />
                </div>
            </div>
        </div>
    );
}

function getContent(element: HTMLElement | null): string {
    const content = element?.getAttribute('content');
    return content ?? '';
}
